import React, { useState, useMemo, useEffect, useRef } from "react";
import moment from "moment";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Link,
  Paper
} from "@mui/material/";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import actions from "../Store/actions";
import { strings } from "../common/i18n";
import styles from "./styles";
import config from "../config";
import { GoogleMap, Marker, Circle, useLoadScript } from "@react-google-maps/api";
import DirectionsCarOutlined from "@mui/icons-material/DirectionsCarOutlined";
import { OverlayView } from "@react-google-maps/api";

export default function SkipTrace() {
  const mapRef = useRef(null);
  const [mapType, setMapType] = useState("roadmap");

  const classes = makeStyles(styles)();
  const dispatch = useDispatch();

  const [vin, setVin] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { data, loading, error } = useSelector((state) => state.skipTrace);

  // Local validation state
  const [formError, setFormError] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMaps.apiKey,
  });

  const onSearch = () => {
    const trimmed = vin.trim();
    const len = trimmed.length;

    if (!trimmed) {
      return;
    } else if (len !== 17) {
      setFormError(true);
      setFormErrorMessage(strings("errors.identifierLengthInvalid", { count: len }));
      setSubmitted(false);
    } else {
      setFormError(false);
      setFormErrorMessage("");
      setSubmitted(true);
      dispatch(actions.skipTrace.create.postRequest({ vin: trimmed }));
    }
  };

  const formatLabel = (key) =>
    (key === "mapViewUrl" || key === "map_view_url")
      ? "Map URL"
      : key.charAt(0).toUpperCase() + key.slice(1);

  // map center
  const hasCoords = data?.latitude != null && data?.longitude != null;
  const center = useMemo(
    () =>
      hasCoords
        ? { lat: Number(data.latitude), lng: Number(data.longitude) }
        : undefined,
    [hasCoords, data?.latitude, data?.longitude]
  );

  useEffect(() => {
    if (isLoaded && mapRef.current && center) {
      mapRef.current.panTo(center);
    }
  }, [isLoaded, center]);

  const renderResult = () => {
    // Show local validation error
    if (formError) {
      return (
        <Typography color="error" className={classes.errorText}>
          {formErrorMessage}
        </Typography>
      );
    }

    if (!submitted) return null;

    if (loading) {
      return (
        <Box className={classes.loading}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (error) {
      const message =
        (typeof error === "string" && /^errors\./.test(error))
          ? strings(error)
          : (typeof error === "object"
              ? (error.message || error.payload || strings("unknown"))
              : String(error));

      return (
        <Typography color="error" className={classes.errorText}>
          {message}
        </Typography>
      );
    }

    if (!data) {
      return <Typography className={classes.errorText}>{strings("noResultsFound")}</Typography>;
    }

    return (
      <Box className={classes.resultsWrap}>
        <Typography variant="subtitle1" className={classes.resultsTitle}>
          {strings("result")}
        </Typography>
        <Divider className={classes.divider} />

        {Object.entries(data)
        .filter(([key]) => !["map_view_url", "mapViewUrl"].includes(key))
        .filter(([key, value]) => !(key === "error" && (value == null || value === "")))
        .map(([key, value]) => (
          <Box key={key} className={classes.row}>
            <Typography variant="body2" className={classes.rowLabel}>
              {key === "timestamp" ? "Last communicated": formatLabel(key)}
            </Typography>
              <Typography variant="body2" className={classes.rowValue}>
                {key === "timestamp" && value && moment(value).isValid()  
                  ? `${moment(value).calendar(null, {
                      sameDay: '[Today at] LT',
                      lastDay: '[Yesterday at] LT',
                      lastWeek: '[Last] dddd [at] LT',
                      sameElse: 'L LT'           
                  })} (${moment(value).fromNow()})`
                  : String(value)}
              </Typography>
          </Box>
        ))}

        {hasCoords && (
          <Box className={classes.mapContainer}>

            {!isLoaded ? (
              <Box className={classes.loading}><CircularProgress size={24} /></Box>
            ) : loadError ? (
              <Typography color="error" className={classes.errorText}>
                strings("errors.googleMapLoadError")
              </Typography>
            ) : (
              <>
                <Box className={classes.mapToggle}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setMapType(mapType === "roadmap" ? "hybrid" : "roadmap")}
                  >
                    {mapType === "roadmap" ? strings("satelliteView") : strings("roadmapView")}
                  </Button>
                </Box>
                
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%", pointerEvents: "auto" }}
                  //defaultZoom={14}
                  defaultCenter={center}
                  mapTypeId={mapType}
                  options={{
                    clickableIcons: false,
                    zoomControl: true,          
                    mapTypeControl: false,
                    draggable:true,
                    scrollwheel:true,
                    disableDoubleClickZoom: false,
                    streetViewControl: true,
                    fullscreenControl: true,
                    gestureHandling: "greedy",  
                    keyboardShortcuts: true,
                  }}
                  onLoad={(map) => { 
                    mapRef.current = map;

                    map.setCenter(center);
                    map.setZoom(14);  

                    const idleOnce = window.google.maps.event.addListenerOnce(map, 'idle', () => {
                      if (!mapRef.current) return;  
                      mapRef.current.setCenter(center);
                    });
                  }}
                >
                  <OverlayView
                    position={center}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                      x: -(width / 2),
                      y: -(height / 1.2),
                    })}
                  >
                    <DirectionsCarOutlined className={classes.carIcon} />
                  </OverlayView>
                  <Circle center={center} radius={500} options={styles.CircleOptions} />
                </GoogleMap>

                <Typography variant="body2" className={classes.mapLink}>
                  <Link
                    href={data.map_view_url || data.mapViewUrl || `https://www.google.com/maps?q=${center.lat},${center.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {strings("viewFullMap")}
                  </Link>
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.container}>
          <Paper elevation={3} className={classes.paper}>
            <Box className={classes.headerRow}>
              <LocationOnIcon color="primary" className={classes.headerIcon} />
              <Typography variant="h6">{strings("skipTrace")}</Typography>
            </Box>

            <Box className={classes.formRow}>
              <TextField
                label={strings("enterVin")}
                value={vin}
                onChange={(e) => setVin(e.target.value)}
                size="small"
                className={classes.vinField}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
              />
              <Button
                variant="contained"
                onClick={onSearch}
                disabled={!vin.trim() || loading}
                className={classes.searchButton}
              >
                {strings("search")}
              </Button>
            </Box>

            {renderResult()}
          </Paper>
        </Box>
      </Box>
    </>
  );
}
